import { Badge } from '@/components/ui/badge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Check, File, X } from 'lucide-react'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import CharacterRoleIcon from 'app/frontend/base/components/CharacterRoleIcon'
import Card from 'app/frontend/base/components/Card'
import { CharacterRole } from 'app/frontend/types/custom'
import { classColours } from 'app/frontend/types/custom/CharacterClass'
import { getSpecializationsByClass } from 'app/frontend/base/services/characterValues'
import { imageUrl } from 'app/frontend/base/helpers'
import { capitalize } from 'lodash'
import { cn } from '@/lib/utils'
import { ActivityAssignmentViewModel } from './activityAssignmentViewModel'

type Props = {
  viewModel: ActivityAssignmentViewModel
  isActive?: boolean
  onClick?: () => void
}

const EncounterSummaryBlock = ({ viewModel, isActive, onClick }: Props) => {
  const {
    activity,
    displayCharacter,
    selection,
    isSelected,
    hasCharacterOverride,
    selectionOverrideDisplay,
    totalAbilityCount,
    isOpenable,
  } = viewModel

  return (
    <Card
      className={cn(
        'self-stretch flex flex-col items-center gap-1.5 p-0 transition-colors',
        onClick && 'cursor-pointer hover:border-primary/50',
        isActive && 'border-primary',
      )}
      onClick={onClick}
    >
      <Tooltip>
        <TooltipTrigger className='inline-flex' asChild>
          <div className='relative'>
            <img
              src={
                activity.encounter?.thumbnailUrl ??
                activity.instance?.tileUrl ??
                imageUrl(`activity-thumbnails/${activity.activityType}.jpg`)
              }
              className='w-10 h-10 rounded-t-md border-b border-border'
            />
            <div className='absolute bottom-px right-1 text-sm font-semibold px-1 py-0.5 leading-none rounded-t-[8px] bg-card-nested/70'>
              {activity.difficulty?.[0].toUpperCase() ?? '-'}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {activity.encounter?.name ||
            activity.instance?.name ||
            capitalize(activity.activityType)}
        </TooltipContent>
      </Tooltip>

      <Badge variant='outline' className='h-6 px-1.5'>
        {isSelected ? (
          <Check className='w-5 h-5 text-success-foreground' />
        ) : (
          <X className='w-5 h-5 text-destructive-foreground' />
        )}
      </Badge>

      {isSelected ? (
        <span className='flex items-center justify-center'>
          {hasCharacterOverride ? (
            <Tooltip>
              <TooltipTrigger className='inline-flex'>
                <span className='relative inline-flex'>
                  <CharacterClassIcon
                    characterClass={displayCharacter.characterClass}
                  />
                  <span className='absolute -right-2 -bottom-2 rounded-full bg-card p-0.5'>
                    <CharacterRoleIcon
                      role={selection.role as CharacterRole}
                      size='small'
                    />
                  </span>
                </span>
              </TooltipTrigger>
              <TooltipContent
                className={classColours[displayCharacter.characterClass].text}
              >
                {displayCharacter.name}
              </TooltipContent>
            </Tooltip>
          ) : selectionOverrideDisplay.showSpecIcon &&
            selectionOverrideDisplay.specIcon ? (
            <BlizzardIcon iconName={selectionOverrideDisplay.specIcon} />
          ) : selectionOverrideDisplay.showRoleIcon ? (
            <CharacterRoleIcon role={selection.role as CharacterRole} />
          ) : selection.specId ? (
            <BlizzardIcon
              iconName={
                getSpecializationsByClass(displayCharacter.classId)[
                  selection.specId
                ]?.icon
              }
            />
          ) : (
            <CharacterRoleIcon role={selection.role as CharacterRole} />
          )}
        </span>
      ) : (
        <span className='h-6' />
      )}

      {isOpenable ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className='inline-flex'>
              <Badge
                variant='outline'
                className={cn(
                  'px-1.5 gap-0.5 mb-1 font-normal',
                  totalAbilityCount === 0 || !isSelected
                    ? 'text-muted-foreground'
                    : 'text-accent-foreground',
                  !isSelected && 'opacity-50 grayscale',
                )}
              >
                <File className='w-3 h-3' /> <span>{totalAbilityCount}</span>
              </Badge>
            </span>
          </TooltipTrigger>
          <TooltipContent>View assignments</TooltipContent>
        </Tooltip>
      ) : null}
    </Card>
  )
}

export default EncounterSummaryBlock
